'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { IndiaPlan } from './types';
import { ChevronRight } from 'assets/icons/ChevronRight';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import { CrossBVModal } from 'assets/icons/CrossBVModal';
import styles from './IndiaLearnMoreModal.module.css';

interface IndiaLearnMoreModalProps {
  plan: IndiaPlan;
  isInCart: boolean;
  onAdd: () => void;
  onRemove: () => void;
  onClose: () => void;
  disabled?: boolean;
}

const IndiaLearnMoreModal: React.FC<IndiaLearnMoreModalProps> = ({
  plan,
  isInCart,
  onAdd,
  onRemove,
  onClose,
  disabled = false,
}) => {
  const [openSections, setOpenSections] = useState({
    about: true,
    whoShouldTake: false,
    keyBiomarkers: true,
    disclaimer: false,
  });

  const [isHovering, setIsHovering] = useState(false);

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const hasDiscount =
    plan.price_details.discount_applied &&
    plan.price_details.original_price !== plan.price_details.price;

  const handleFooterButtonClick = () => {
    if (disabled) {
      // Check if on mobile (viewport width <= 768px)
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // Close modal first
        onClose();

        // Wait for modal close animation, then scroll and focus
        setTimeout(() => {
          // Find the pincode input element
          const pincodeInput = document.getElementById(
            'blood-vision-pincode-input',
          );

          if (pincodeInput) {
            // Scroll to the input with smooth behavior
            pincodeInput.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });

            // Focus the input after a small delay to ensure scroll completes
            setTimeout(() => {
              pincodeInput.focus();
            }, 300);
          }
        }, 400); // Match modal close animation duration
      }
      return;
    }

    if (isInCart) {
      onRemove();
    } else {
      onAdd();
    }
  };

  return (
    <div className={styles.modalContainer}>
      <button className={styles.closeButton} onClick={onClose}>
        <CrossBVModal />
      </button>

      {/* Header Section - Fixed at top */}
      <div className={styles.headerSection}>
        <div className={styles.thumbnailContainer}>
          {plan.icon_url ? (
            <Image src={plan.icon_url} alt={plan.name} width={64} height={64} />
          ) : (
            plan.name.charAt(0).toUpperCase()
          )}
        </div>
        <div className={styles.headerInfo}>
          <h2 className={styles.planName}>{plan.name}</h2>
          <div className={styles.headerPriceRow}>
            <span className={styles.headerCurrentPrice}>
              ₹{plan.price_details.price.toLocaleString('en-IN')}
            </span>
            {hasDiscount && (
              <span className={styles.headerOriginalPrice}>
                ₹{plan.price_details.original_price.toLocaleString('en-IN')}
              </span>
            )}
          </div>
          {plan.price_per_month && (
            <span className={styles.headerPerMonth}>
              ₹{plan.price_per_month.toLocaleString('en-IN')} per month
            </span>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className={styles.modalContent}>
        {/* About Section */}
        {plan.description && (
          <>
            <div className={styles.descriptionSection}>
              <button
                className={styles.sectionHeader}
                onClick={() => toggleSection('about')}
              >
                <h3 className={styles.sectionTitle}>About</h3>
                <div
                  className={styles.chevronIcon}
                  data-open={openSections.about}
                >
                  <ChevronRight fill="#000000" />
                </div>
              </button>
              <div
                className={styles.collapsibleContent}
                data-open={openSections.about}
              >
                <div
                  className={styles.collapsibleInner}
                  data-open={openSections.about}
                >
                  <p className={styles.descriptionText}>{plan.description}</p>
                </div>
              </div>
            </div>
            <div className={styles.divider} />
          </>
        )}

        {/* Who Should Take This Test Section */}
        {/* <DescriptionSection>
            <SectionHeader onClick={() => toggleSection('whoShouldTake')}>
              <SectionTitle>Who should take this test</SectionTitle>
              <ChevronIcon $isOpen={openSections.whoShouldTake}>
                <ChevronRight fill="#000000" />
              </ChevronIcon>
            </SectionHeader>
            <CollapsibleContent $isOpen={openSections.whoShouldTake}>
              <CollapsibleInner $isOpen={openSections.whoShouldTake}>
                <DetailsList>
                  <DetailItem>
                    <div>
                      <DetailItemTitle>
                        Anyone looking to optimize their health
                      </DetailItemTitle>
                    </div>
                  </DetailItem>
                  <DetailItem>
                    <div>
                      <DetailItemTitle>
                        Individuals experiencing fatigue or low energy
                      </DetailItemTitle>
                    </div>
                  </DetailItem>
                  <DetailItem>
                    <div>
                      <DetailItemTitle>
                        People wanting to track their wellness metrics
                      </DetailItemTitle>
                    </div>
                  </DetailItem>
                  <DetailItem>
                    <div>
                      <DetailItemTitle>
                        Those seeking preventive health insights
                      </DetailItemTitle>
                    </div>
                  </DetailItem>
                </DetailsList>
              </CollapsibleInner>
            </CollapsibleContent>
          </DescriptionSection>
          <Divider /> */}

        {/* Key Biomarkers Section */}
        {plan.biomarker_summary && plan.biomarker_summary.length > 0 && (
          <>
            <div className={styles.descriptionSection}>
              <button
                className={styles.sectionHeader}
                onClick={() => toggleSection('keyBiomarkers')}
              >
                <h3 className={styles.sectionTitle}>Key biomarkers tested</h3>
                <div
                  className={styles.chevronIcon}
                  data-open={openSections.keyBiomarkers}
                >
                  <ChevronRight fill="#000000" />
                </div>
              </button>
              <div
                className={styles.collapsibleContent}
                data-open={openSections.keyBiomarkers}
              >
                <div
                  className={styles.collapsibleInner}
                  data-open={openSections.keyBiomarkers}
                >
                  <ul className={styles.detailsList}>
                    {plan.biomarker_summary.map((biomarker, index) => (
                      <li className={styles.detailItem} key={index}>
                        <div>
                          <span className={styles.detailItemTitle}>
                            {biomarker.title}
                            {biomarker.count &&
                              ` - ${biomarker.count} Parameters`}
                          </span>
                          {biomarker.description && (
                            <>
                              {' - '}
                              <span className={styles.detailItemDescription}>
                                {biomarker.description}
                              </span>
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className={styles.divider} />
          </>
        )}

        {/* Disclaimer Section */}
        <div className={styles.descriptionSection}>
          <button
            className={styles.sectionHeader}
            onClick={() => toggleSection('disclaimer')}
          >
            <h3 className={styles.sectionTitle}>Disclaimer</h3>
            <div
              className={styles.chevronIcon}
              data-open={openSections.disclaimer}
            >
              <ChevronRight fill="#000000" />
            </div>
          </button>
          <div
            className={styles.collapsibleContent}
            data-open={openSections.disclaimer}
          >
            <div
              className={styles.collapsibleInner}
              data-open={openSections.disclaimer}
            >
              <p className={styles.descriptionText}>
                These tests are for informational purposes only and should not
                replace professional medical advice. Please consult with a
                healthcare provider for proper interpretation of your results
                and any health concerns.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.stickyFooter}>
        <div
          className={styles.buttonWrapper}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div className={styles.tooltip} data-show={disabled && isHovering}>
            Please try with 6 digit valid pincode
          </div>
          <PrimaryButton
            className={styles.footerButton}
            data-in-cart={isInCart}
            data-disabled={disabled}
            onClick={handleFooterButtonClick}
            type="button"
          >
            <span className={styles.footerButtonLabel}>
              {isInCart ? 'Added to cart' : 'Add to cart'}
            </span>
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default IndiaLearnMoreModal;
